import {
  getClickCancelOOPModalEvent,
  getClickContinueOOPModalEvent,
  getShowOOPModalEvent,
  isCorpTenant,
  useMultiroomCountText,
  useShowPolicyBanner,
} from "@capone/common";
import { useExperimentsById } from "@capone/experiments";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import {
  BackToTopButton,
  getFirstPayNowRoomAndProductIndex,
  HotelShopRoomTypePickerEnum,
  HotelShopRoomTypePickerRedesign,
  HotelShopRoomTypePickerVariant,
  Icon,
  IconName,
  MultiroomSuffixText,
  SanitizedHtml,
  TrackingEventControlType,
} from "halifax";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { RouteComponentProps } from "react-router";
import {
  ModalScreens,
  SELECT_HOTEL_ROOM,
  SelectedTravelOfferScreen,
  ViewedCorpRateDescriptorEntryPoints,
} from "redmond";
import { ClientContext } from "../../../../App";
import { config } from "../../../../api/config";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import {
  AVAILABLE,
  CONTROL,
  GLOBAL_MOBILE_NAV_EXPERIMENT,
  MOBILE_RECENTLY_VIEWED_HOTELS,
  MOBILE_RECENTLY_VIEWED_HOTELS_VARIANTS,
  SHOW_MEALPLAN,
  RECENTLY_VIEWED_MASTER_V1,
  SIMILAR_HOTELS_EXPERIMENT,
  SIMILAR_HOTELS_VARIANTS,
  TRAVEL_WALLET_OFFER_EXPERIMENT,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../../../context/experiments";
import { TravelWalletDetailsBanner } from "../../../travel-wallet/components/TravelWalletDetailsBanner";
import { getEarnTagText } from "../../textConstants";
import { shouldTrackLodgingLocation } from "../../utils/shouldTrackLodgingLocation";
import { MobileShopHeader } from "../MobileShopHeader";
import { MobileRoomPickerRedesignConnectorProps } from "./container";
import { onOpenCompareBarTooltip } from "../../../../utils/events";
import "./styles.scss";

export interface IMobileRoomPickerRedesignProps
  extends MobileRoomPickerRedesignConnectorProps,
    RouteComponentProps {
  roomInfoProductsType?: HotelShopRoomTypePickerVariant;
  handleReadyToRedirect?: (
    trackingEventControl?: TrackingEventControlType,
  ) => void;
  nonRefundablePolicyOverrideText?: string;
}

export const MobileRoomPicker = (props: IMobileRoomPickerRedesignProps) => {
  const {
    checkinDate,
    checkoutDate,
    history,
    fetchHotelShop,
    chosenProduct,
    selectRoomType,
    selectedLodging,
    chosenRoomInfoIndex,
    roomsCount,
    handleReadyToRedirect,
    showOfferBasedOnSelectedLodging,
    roomInfoProductsType,
    isFintechHotelUpdatedUxEnabled,
    isHotelCfarModelV1Enabled,
    isHotelCfarModelV1RefundableRoomUpdatedCopy,
    isHotelCfarModelV1RefundableRoomUpdatedUX,
    canEarnRewards,
    selectedRewardsAccount,
  } = props;
  const isFirstUpdate = useRef<boolean>(true);
  const expState = useExperiments();
  const travelWalletOffer = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_OFFER_EXPERIMENT,
  );
  const isTravelWalletOfferExperiment = React.useMemo(
    () => travelWalletOffer === AVAILABLE,
    [travelWalletOffer],
  );

  const similarHotelsVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    SIMILAR_HOTELS_EXPERIMENT,
    SIMILAR_HOTELS_VARIANTS,
  );

  const showEarnEnhancement =
    !!selectedRewardsAccount &&
    !!selectedRewardsAccount.earn.hotelsMultiplier &&
    canEarnRewards;

  const recentlyViewedMobileP0Variant = getExperimentVariantCustomVariants(
    expState.experiments,
    MOBILE_RECENTLY_VIEWED_HOTELS,
    MOBILE_RECENTLY_VIEWED_HOTELS_VARIANTS,
  );

  const recentlyViewedExperiment = getExperimentVariant(
    expState.experiments,
    RECENTLY_VIEWED_MASTER_V1,
  );
  const isRecentlyViewedV1Experiment = useMemo(
    () => recentlyViewedExperiment === AVAILABLE,
    [recentlyViewedExperiment],
  );

  const globalMobileNavExperimentVariant = getExperimentVariant(
    expState.experiments,
    GLOBAL_MOBILE_NAV_EXPERIMENT,
  );
  const isGlobalMobileNavExperiment = React.useMemo(
    () => globalMobileNavExperimentVariant === AVAILABLE,
    [globalMobileNavExperimentVariant],
  );

  const showGlobalMobileNavExperience =
    isGlobalMobileNavExperiment && !isCorpTenant(config.TENANT);

  const isMultiroomAmadeus =
    useExperimentsById("corp-amadeus-multiroom")?.variant === "available";

  const { isAutoApprovalEnabled } = useContext(ClientContext);

  const modalType = isAutoApprovalEnabled
    ? "out_of_policy_auto"
    : "out_of_policy_24hr_review";

  const isShowMealPlanExperiment =
    getExperimentVariant(expState.experiments, SHOW_MEALPLAN) === AVAILABLE;

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  useEffect(() => {
    if (checkinDate && checkoutDate) {
      // skip the first update
      if (isFirstUpdate.current) {
        isFirstUpdate.current = false;
      } else {
        fetchHotelShop(history, {
          forceCallHotelAvailability: true,
          fetchSimilarHotels: similarHotelsVariant !== CONTROL,
          includeLocationSearchTerm:
            isRecentlyViewedV1Experiment &&
            recentlyViewedMobileP0Variant !== CONTROL &&
            shouldTrackLodgingLocation(history.location.search),
        });
      }
    }
  }, [checkinDate, checkoutDate]);

  useEffect(() => {
    if (!chosenProduct) {
      const { roomInfoIndex, productIndex } = getFirstPayNowRoomAndProductIndex(
        props.roomInfoProducts,
      );
      selectRoomType(roomInfoIndex, productIndex);
    }
    setTimeout(() => {
      const id = "chosen-room";
      const yOffset = -122.5;
      const element = document.getElementById(id);
      const y =
        (element?.getBoundingClientRect().top ?? 0) +
        window.pageYOffset +
        yOffset;
      if (chosenRoomInfoIndex || chosenRoomInfoIndex === 0) {
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }, 50);
  }, []);
  const [showOfferBanner, setShowOfferBanner] = useState(false);

  useEffect(() => {
    setShowOfferBanner(
      isTravelWalletOfferExperiment && showOfferBasedOnSelectedLodging,
    );
  }, [showOfferBasedOnSelectedLodging]);

  const { policies } = useContext(ClientContext);
  const showPolicyBanner = useShowPolicyBanner(policies);

  return (
    <Box>
      <MobileShopHeader />
      {showOfferBanner && (
        <TravelWalletDetailsBanner
          onDismiss={() => setShowOfferBanner(false)}
          offer={selectedLodging?.bestOfferThisLodging!}
          className="mobile-hotel-shop-offer"
          screen={SelectedTravelOfferScreen.HOTEL_DETAILS}
          bannerTextType="shop"
        />
      )}
      <HotelShopRoomTypePickerRedesign
        {...props}
        roomInfoProductsType={
          roomInfoProductsType ?? {
            roomInfoProducts: props.roomInfoProducts,
            variant: HotelShopRoomTypePickerEnum.Default,
          }
        }
        isMobile
        className="b2b"
        onClickContinue={(productTrackingProperties?: object) => {
          if (handleReadyToRedirect) {
            handleReadyToRedirect({
              [SELECT_HOTEL_ROOM]: {
                properties: {
                  ...(productTrackingProperties ?? {}),
                },
              },
            });
          }
        }}
        reserveRoomPrefixText={
          roomsCount > 1 ? (
            <Typography className="reserve-text">
              Reserve{" "}
              <span className={clsx("rooms-count")}>{roomsCount} Rooms</span>{" "}
              for
            </Typography>
          ) : (
            <Typography className="reserve-text">Select room for</Typography>
          )
        }
        mobileContinueButtonText={
          roomsCount > 1 ? <span>Continue</span> : undefined
        }
        suffixText={<MultiroomSuffixText roomsCount={roomsCount} />}
        earnTagContent={
          showEarnEnhancement ? (
            <>
              <Icon name={IconName.StarIcon} />
              <SanitizedHtml
                as={Typography}
                className="earn-tag-text"
                html={getEarnTagText(
                  selectedRewardsAccount.earn.hotelsMultiplier,
                  selectedRewardsAccount.rewardsBalance.currencyDescription ??
                    selectedRewardsAccount.rewardsBalance.currency,
                )}
              />
            </>
          ) : undefined
        }
        earnTagClassName={showEarnEnhancement ? "b2b" : undefined}
        variant={
          isFintechHotelUpdatedUxEnabled ? "refundable-room-ux" : "default"
        }
        isShowMealPlanExperiment={isShowMealPlanExperiment}
        isHotelCfarModelV1Enabled={isHotelCfarModelV1Enabled}
        isHotelCfarModelV1RefundableRoomUpdatedCopy={
          isHotelCfarModelV1RefundableRoomUpdatedCopy
        }
        isHotelCfarModelV1RefundableRoomUpdatedUX={
          isHotelCfarModelV1RefundableRoomUpdatedUX
        }
        isApprovalRequired={!isAutoApprovalEnabled}
        onShowOutOfPolicyModal={() =>
          trackEvent(
            getShowOOPModalEvent(ModalScreens.HOTELS_SHOP, "hotels", modalType),
          )
        }
        onClickOutOfPolicyContinue={() =>
          trackEvent(
            getClickContinueOOPModalEvent(
              ModalScreens.HOTELS_SHOP,
              "hotels",
              modalType,
            ),
          )
        }
        onClickOutOfPolicyCancel={() =>
          trackEvent(
            getClickCancelOOPModalEvent(
              ModalScreens.HOTELS_SHOP,
              "hotels",
              modalType,
            ),
          )
        }
        isGlobalMobileNavExperiment={showGlobalMobileNavExperience}
        roomNameSuffixText={
          isMultiroomAmadeus ? useMultiroomCountText(roomsCount) : ""
        }
        roomsCount={roomsCount}
        onViewCorpCompareBar={onOpenCompareBarTooltip(
          ViewedCorpRateDescriptorEntryPoints.HOTELS_ROOM_DETAILS,
        )}
        showPolicyBanner={showPolicyBanner}
        rewardsAccount={selectedRewardsAccount}
        isXmodRedesign={isXmodRedesign}
      />
      {showGlobalMobileNavExperience && <BackToTopButton />}
    </Box>
  );
};
